import { Box, Typography } from '@mui/material';
import { DirectDepositFormPayload, submitDirectDepositForm } from 'core/API/documents';
import { fileUpload } from 'core/API/events';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import {
	DelayButton,
	FormAlert,
	FormGroupSection,
	SignAndSubmit,
	NextAndCancel,
} from 'module/documents/components/components';
import DynamicFormComponent, {
	FieldRenderer,
	GridWrapper,
} from 'module/documents/components/formTabs/DynamicFormComponent';
import { EmployerFields } from 'module/documents/components/formTabs/EmployerFields';
import { useDocumentContext } from 'module/documents/context';
import {
	CommonFieldsFormIndexed,
	DirectDepositFormData,
	DirectDepositFormDataIndexed,
} from 'module/documents/data';
import { FileUploadField } from 'module/documents/tabs/HumanResource/components';
import { filterFields } from 'module/documents/utils';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import { validateForm } from 'module/documents/utils';
import { useMemo, FC } from 'react';
import { handleDirectDepositPDFGeneration } from 'shared/utils/dates/Direct_deposit_pdfGenerator';

const readOnlyFields = ['first_name', 'middle_initial', 'last_name', 'address', 'city', 'state', 'restaurant_name'];

const step1Fields = filterFields(CommonFieldsFormIndexed, {
	include: ['first_name', 'middle_initial', 'last_name'],
}).map((field) => ({
	...field,
	readOnly: readOnlyFields.includes(field.value),
}));

const bankFields = filterFields(DirectDepositFormDataIndexed, {
	include: [
		'bank_name',
		'routing_number',
		'account_number',
		'account_type',
		'change_payroll_account',
	],
}).map((field) => {
	switch (field.value) {
		case 'routing_number':
			return {
				...field,
				pattern: '^\\d{9}$',
				maxLength: 9,
				customErrorMessage: 'Routing number must be exactly 9 digits',
				numericOnly: true,
			};
		case 'account_number':
			return {
				...field,
				pattern: '^\\d+$',
				customErrorMessage: 'Account number must contain only digits',
				numericOnly: true,
			};
		default:
			return field;
	}
});

const restaurantNameField = {
	...DirectDepositFormDataIndexed['restaurant_name'],
	readOnly: readOnlyFields.includes('restaurant_name'),
  };

interface DirectDepositFormProps {
	employeeSignatureFile: File | null;
}
export const DirectDepositForm: FC<DirectDepositFormProps> = ({
	employeeSignatureFile
}) => {

	const { formik, selectedEmployee, recordFormSubmission, submittedForms, setSubmittedForms, setActiveTabIndex  } = useDocumentContext();
	const { workplaceId, organization } = useWorkplacesContext();

	const { showToast } = useToastContext();
	const maxSizeMB = 20;
	const onFileChange = (fieldName: string, file: File) => {
		formik.setFieldValue(fieldName, file);
	};

	const disableNext = useMemo(() => {
		const formStatus = validateForm(formik, DirectDepositFormData);
		return !formStatus.valid;
		}, [formik.values]);

	const handleSubmit = async () => {

		const fileToBase64 = (file: File): Promise<string> => {
			return new Promise((resolve, reject) => {
				const reader = new FileReader();
				reader.readAsDataURL(file);
				reader.onload = () => resolve(reader.result as string);
				reader.onerror = reject;
			});
		};

		// Prepare file upload promises
		const uploadFile = async (file: File | null) => {
			if (file) {
				const formData = new FormData();
				formData.append('file', file);
				return await fileUpload(formData);
			}
			return null;
		};

		const checkFile = await uploadFile(formik.values.check_file);

		const imageOverlays = [];

		if (formik.values.check_file) {
			const base64 = await fileToBase64(formik.values.check_file);
			imageOverlays.push({
				imageData: base64,
				position: {
					x: 76,   // ← adjust to your desired position
					y: 240,  // ← adjust to your desired position
					width: 465, // optional
					height: 190  // optional
					},
					pageNum: 0,
			});
		}

		const payload: DirectDepositFormPayload = {
			bank_name: formik.values.bank_name,
			routing_number: formik.values.routing_number,
			account_number: formik.values.account_number,
			account_type: String(formik.values.account_type),
			change_payroll_account: formik.values.change_payroll_account,
			employee_file: {
				id: checkFile?.id,
			},
			workplace: {
				id: workplaceId,
			},
			employee: {
				id: selectedEmployee.id,
			},
		};

		try {
			await submitDirectDepositForm(payload, workplaceId);
			showToast('success', 'Form has been submitted!');
			//----------------------Editing existing PDF and sending to API------------------
			try {
	
				handleDirectDepositPDFGeneration({
					formik,
					employeeSignatureFile: employeeSignatureFile,
					workplaceName: selectedEmployee?.name,
					organizationName: organization.name,
					employeeEmail: selectedEmployee.email,
					imageOverlays:imageOverlays
				});
				
			} catch (error) {
				console.error("Error filling PDF:", error);
			}
		//---------------Editing existing PDF and sending to API ENDS HERE --------------  
			recordFormSubmission('directDepositId');
			setSubmittedForms(prev => ({
				...prev,
				direct_deposit_form_submitted: true,
			}));
			setActiveTabIndex(13);
		} catch (error: any) {
			console.error('Error submitting form:', error.error);
			alert('Something went wrong while submitting the form.');
		}
	};

	const isImageFileValid = (file: File | null | undefined): boolean => {
		if (!file) return false;
	  
		const isImage = file.type.startsWith('image/');
		const isSizeValid = file.size <= maxSizeMB * 1024 * 1024;
	  
		return isImage && isSizeValid;
	};
	return (
		<>
			<Box mb={2}>
				<Typography variant="h4" mb="24px">
					Direct Deposit Form
				</Typography>

				<Typography variant="body1">{DirectDepositFormData.title}</Typography>
			</Box>

			<Box display="flex" flexDirection="column" gap={2}>
				<FormGroupSection>
					<FormAlert severity="error">
						<Typography variant="h6">
							<strong>The business will not process this form without:</strong>
						</Typography>
						- All fields completed!
						<br />- A voided check attached to this form. Deposit slips will not be accepted! (If a
						voided check is not available, please attach a bank-issued direct deposit form.)
					</FormAlert>

					<DynamicFormComponent>
						<GridWrapper field={restaurantNameField} index={0}>
							<FieldRenderer
								field={restaurantNameField}
								formik={formik}
							/>
						</GridWrapper>
					</DynamicFormComponent>
				</FormGroupSection>
				<FormGroupSection title="Bank fields">
					<DynamicFormComponent>
						{bankFields.map((field, index) => (
							<GridWrapper key={index} field={field} index={index}>
								<FieldRenderer field={field} formik={formik} />
							</GridWrapper>
						))}
					</DynamicFormComponent>
				</FormGroupSection>
				<FormGroupSection>
					<FormAlert severity="warning">
						<Typography variant="h6" gutterBottom>
							<strong>Authorization for Payroll Direct Deposit</strong>
						</Typography>
						<Typography variant="body1" gutterBottom>
							I hereby authorize Restaurant Accounting Services, Inc. to initiate debit/credit
							entries to my checking/savings account indicated above at the depository financial
							institution (bank) named above, hereinafter called <strong>DEPOSITORY</strong>, and to
							debit/credit the same to such account. <br />I acknowledge that the origination of ACH
							transactions to my account must comply with the provisions of the applicable laws.
						</Typography>
						<Typography variant="body1">
							This authorization is to remain in full force and effect until Restaurant Accounting
							Services, Inc. has received notification of its termination in such time and in such
							manner as to afford Restaurant Accounting Services, Inc. and{' '}
							<strong>DEPOSITORY</strong> a reasonable opportunity to act on it.
						</Typography>
					</FormAlert>
					<DynamicFormComponent>
						{step1Fields.map((field, index) => (
							<GridWrapper key={index} field={field} index={index}>
								<FieldRenderer field={field} formik={formik} />
							</GridWrapper>
						))}
					</DynamicFormComponent>
					<DynamicFormComponent>
						<GridWrapper field={DirectDepositFormDataIndexed['ssn_last_4']} index={0}>
							<FieldRenderer field={DirectDepositFormDataIndexed['ssn_last_4']} formik={formik} />
						</GridWrapper>
					</DynamicFormComponent>
				</FormGroupSection>
				<FormGroupSection>
					<DynamicFormComponent flex="1" marginBottom="12px">
						<FormAlert severity="warning">
							Attach direct deposit check or a bank-issued direct deposit form
						</FormAlert>
					</DynamicFormComponent>
					<FileUploadField
						id="check_file"
						existingFilePath={formik.values.check_file?.path}
						onFileChange={(file: File) => onFileChange('check_file', file)}
					/>
				</FormGroupSection>
			</Box>

			<Box paddingY="24px">
				<SignAndSubmit onSubmit={handleSubmit} 
				extraValidation={[
					{
					field: `Voided check image must be a valid image under ${maxSizeMB}MB`,
					value: isImageFileValid(formik.values.check_file),
					},
				]}
				  formData={DirectDepositFormData} />
			</Box>

			<DirectDepositFormCertification />
			<NextAndCancel
				nextTabIndex={13}
				formData={DirectDepositFormData}
				isNextDisabled={!submittedForms.direct_deposit_form_submitted}
			/>
		</>
	);
};

const DirectDepositFormCertification = () => {
	const { formik, isEmployer } = useDocumentContext();

	return (
		<></>
		// <FormGroupSection show={isEmployer}>
		// 	<FormGroupSection
		// 		title="Employer Information"
		// 		isEmployer={true}
		// 		marginTop="24px"
		// 		marginBottom="12px"
		// 	>
		// 		<EmployerFields fieldValues={['employer_title']} />
		// 	</FormGroupSection>
		// 	<Box paddingY="8px">
		// 		<DelayButton
		// 			variant="contained"
		// 			onClick={() => {
		// 				formik.setFieldValue('signature_approved', true);
		// 			}}
		// 			style={{ width: 'max-content', color: "white", backgroundColor: 'rgb(188, 39, 39)',  }}
		// 		>
		// 			Employer sign
		// 		</DelayButton>
		// 	</Box>
		// </FormGroupSection>
	);
};
